import { z } from "zod";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePrices } from "@/lib/hooks";
import { formatCurrency } from "@/lib/formatters";
import type { CreateOrderInput } from "@/lib/api";
import { usePaymentSubmission } from "@/lib/payment-flow";
import {
  ATTESTATION_REQUIRED_MESSAGE,
  addCardIssues,
  buildPaymentFields,
  employeeRowSchema,
  hasAttestedRow,
  paymentFormShape,
  scrollToFirstError,
  useRosterCheckoutSuccess,
} from "@/lib/checkout-form";
import { EmployeeFields } from "@/components/EmployeeFields";
import { OrderSummary } from "@/components/OrderSummary";
import {
  CheckoutPaymentSection,
  CheckoutSubmitSection,
} from "@/components/RosterCheckoutParts";

/** Add employees to the roster: the public checkout's employee rows (per-row
 *  product, scoped attestation) for a vendor who is already logged in, so there is
 *  no registration or login section and no company block. */
const rosterOrderSchema = z
  .object({
    employees: z
      .array(employeeRowSchema)
      .min(1, "At least one employee is required")
      .max(50, "Maximum 50 employees per order"),
    // Required, and true, only while at least one row is already-cleared; the rule
    // lives in superRefine because an RHF `validate` rule never runs under a resolver.
    attestationAccepted: z.boolean(),
    ...paymentFormShape,
  })
  .superRefine((data, ctx) => {
    if (hasAttestedRow(data.employees) && !data.attestationAccepted) {
      ctx.addIssue({
        code: "custom",
        message: ATTESTATION_REQUIRED_MESSAGE,
        path: ["attestationAccepted"],
      });
    }
    addCardIssues(data.card, ctx);
  });

type RosterOrderFormData = z.infer<typeof rosterOrderSchema>;

function buildRosterOrder(data: RosterOrderFormData, idempotencyKey: string): CreateOrderInput {
  const order: CreateOrderInput = {
    type: "employee_addition",
    employees: data.employees,
    ...buildPaymentFields(data, idempotencyKey),
  };
  // The server stamps the attestation's copyVersion and time; the client sends
  // only the boolean, and only while a row is marked already-cleared.
  if (hasAttestedRow(data.employees) && data.attestationAccepted) {
    order.attestationAccepted = true;
  }
  return order;
}

export function RosterOrderPage(): React.ReactElement {
  const { data: prices, isError: priceFailed } = usePrices();
  const onSuccess = useRosterCheckoutSuccess();

  const methods = useForm<RosterOrderFormData>({
    resolver: zodResolver(rosterOrderSchema),
    defaultValues: {
      employees: [{ firstName: "", lastName: "", product: "bcb" }],
      attestationAccepted: false,
      billingAddress: { street: "", city: "", state: "", zip: "" },
      paymentMethod: "credit_card",
      card: { cardNumber: "", expireMonth: "", expireYear: "", cvv: "", nameOnCard: "" },
    },
  });

  // Literal flow: this page's own key slot, never the public checkout's.
  const payment = usePaymentSubmission<RosterOrderFormData>({
    flow: "roster-addition",
    buildOrder: buildRosterOrder,
    onSuccess,
  });

  const employees = useWatch({ control: methods.control, name: "employees" });
  const badgeCount = (employees ?? []).filter((e) => e.product === "badge_attested").length;
  const bcbCount = (employees?.length ?? 1) - badgeCount;
  // No fallback price: until the prices load the order cannot be placed.
  const total =
    prices === undefined ? undefined : bcbCount * prices.bcbPrice + badgeCount * prices.badgePrice;

  return (
    <div className="mx-auto max-w-[640px]">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Add employees</h1>
      <p className="mb-6 text-sm text-gray-600">
        Order background checks and badges for new employees. They are added to My Employees
        once the order is placed.
      </p>

      <FormProvider {...methods}>
        <form
          onSubmit={(e) => void methods.handleSubmit(payment.submit, scrollToFirstError)(e)}
          noValidate
          className="space-y-8"
        >
          <section>
            <EmployeeFields />
          </section>

          <CheckoutPaymentSection />

          <section>
            <OrderSummary bcbCount={bcbCount} badgeCount={badgeCount} />
          </section>

          <CheckoutSubmitSection
            payment={payment}
            priceFailed={priceFailed}
            label={total === undefined ? undefined : `Place Order — ${formatCurrency(total)}`}
          />
        </form>
      </FormProvider>
    </div>
  );
}
